import { DivConst } from 'constants/divConst';

import type { useApiState } from 'hooks/useApiState';
import type { AcctStateType } from 'stores/atoms/acctState';
import type { QuizResponseType } from 'types/api/quizResponseType';
import type { QuizEditFormStateType } from 'types/page/quizEditFormStateType';
import { getProductionUrl } from 'utils/terms';

// ------------------------------------------------------------------ //
// 型定義
// ------------------------------------------------------------------ //

type UseApiStateReturn = ReturnType<typeof useApiState>;

/**
 * postQuizRegister の引数型から companyId / quizStatusDiv を除いた共通パラメータ型。
 * postQuizUpdate へのスプレッドにも利用する。
 * reportMailDescribeHtml は postQuizRegister の型定義に含まれないが既存実装で送信しているため
 * intersection で補完する。
 */
export type QuizCommonParam = Omit<
  Parameters<UseApiStateReturn['postQuizRegister']>[0],
  'companyId' | 'quizStatusDiv'
> & {
  reportMailDescribeHtml?: string | null;
};

/**
 * postQuizAiQuestions の request（currentContent）型。
 */
export type QuizAiContentInput = Parameters<
  UseApiStateReturn['postQuizAiQuestions']
>[0]['currentContent'];

/**
 * postQuizAiQuestions のレスポンス（answerContent）から取り出した問題アイテム型。
 */
export type AiQuizQuestion = NonNullable<
  Awaited<ReturnType<UseApiStateReturn['postQuizAiQuestions']>>['answerContent']
>['questions'][number];

// ------------------------------------------------------------------ //
// convertFormValuesToParam
// ------------------------------------------------------------------ //

/**
 * select / radio 由来の文字列値を number | null に型強制する。
 * HTML フォームは select の value を常に文字列で返すため、API スキーマ（number | null）に揃える。
 * - null / undefined / 空文字 → null
 * - それ以外 → Number() で数値化
 */
function toNumberOrNull(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  return Number(value);
}

/**
 * react-hook-form のフォーム値を API 送信パラメータに変換する。
 * - popupImageKey は含まない（hook 側で別途セットする）
 * - questionImageKey / answerImageKey / result.imageKey はファルシー値を null に変換する
 * - lineFlag は DivConst の数値から boolean に変換する
 * - respondentMailDiv / respondentFields の div 値は toNumberOrNull で number | null に統一する
 *   （HTML select は値を文字列で返すため、API スキーマ型に合わせて変換する）
 * - reportMailSignatureHtml / reportMailDescribeHtml / description / questions[].descriptionHtml /
 *   questions[].reportMailDescriptionHtml / results[].descriptionHtml は undefined の場合に
 *   null を明示的に送る
 *   （undefined のままだと JSON.stringify でキーが脱落し API required 検証で 400 になる）
 */
export function convertFormValuesToParam(values: QuizEditFormStateType): QuizCommonParam {
  return {
    title: values.title,
    titleHiddenFlag: values.titleHiddenFlag,
    description: values.description ?? null,
    respondentMailDiv: toNumberOrNull(values.respondentMailDiv),
    imageKey: values.imageKey,
    reportMailSubject: values.reportMailSubject,
    reportMailSenderName: values.reportMailSenderName,
    reportMailSignatureHtml: values.reportMailSignatureHtml ?? null,
    reportMailDescribeHtml: values.reportMailDescribeHtml ?? null,
    quizTemplateId: values.quizTemplateId,
    publishStartAt: values.publishStartAt,
    publishEndAt: values.publishEndAt,
    noIndexFlag: values.noIndexFlag,
    benchmarkContactListId: values.benchmarkContactListId,
    gtmId: values.gtmId,
    respondentFields: values.respondentFields.map(field => ({
      getFlag: field.getFlag,
      respondentFieldDiv: toNumberOrNull(field.respondentFieldDiv),
      respondentStandardFieldDiv: toNumberOrNull(field.respondentStandardFieldDiv),
      label: field.label,
      placeholder: field.placeholder,
      respondentCustomFieldDiv: toNumberOrNull(field.respondentCustomFieldDiv),
      respondentCustomFieldInputDiv: toNumberOrNull(field.respondentCustomFieldInputDiv),
      maxLength: toNumberOrNull(field.maxLength),
    })),
    lineFlag: Number(values.lineFlag) === DivConst.items.lineFlag.enable.value,
    lineUrl: values.lineUrl,
    questions: values.questions.map(question => ({
      title: question.title,
      descriptionHtml: question.descriptionHtml ?? null,
      reportMailDescriptionHtml: question.reportMailDescriptionHtml ?? null,
      questionImageKey: question.questionImageKey || null,
      answerImageKey: question.answerImageKey || null,
      options: question.options.map(option => ({
        title: option.title,
        correctFlag: option.correctFlag,
      })),
    })),
    results: values.results.map(result => ({
      scoreThreshold: result.scoreThreshold,
      title: result.title,
      descriptionHtml: result.descriptionHtml ?? null,
      imageKey: result.imageKey || null,
    })),
  };
}

// ------------------------------------------------------------------ //
// buildInitialQuizForm
// ------------------------------------------------------------------ //

/**
 * 新規クイズ作成時のフォーム初期値を生成する。
 * reportMailSenderAddress は hook 側で config.ses.from.address から設定するため含まない。
 */
export function buildInitialQuizForm(acct: AcctStateType): Partial<QuizEditFormStateType> {
  const company = acct.company;

  const termsUrl =
    company.termsInputDiv === DivConst.items.termsInputType.url.value
      ? (company.termsUrl ?? '')
      : getProductionUrl(company.cd);

  return {
    privacyPolicyUrl: company.privacyPolicyUrl ?? '',
    termsUrl,
    copyright: company.copyright ?? '',
    reportMailReplyAddress: company.reportMailReplyAddress ?? '',
    reportMailSenderName: company.reportMailSenderName ?? '',
    reportMailSignatureHtml: company.reportMailSignatureHtml ?? '',
    publishStartAt: null,
    publishEndAt: null,
    noIndexFlag: false,
    quizTemplateId: 1,
    respondentFieldGroupId: null,
    respondentFields: [],
    popupImageKey: null,
    questions: [
      {
        options: [
          { correctFlag: true },
          { correctFlag: false },
          { correctFlag: false },
          { correctFlag: false },
        ],
      },
    ],
    results: [
      {
        scoreThreshold: 0,
      },
    ],
  };
}

// ------------------------------------------------------------------ //
// buildEditFormFromResponse
// ------------------------------------------------------------------ //

/**
 * API レスポンスをフォーム state に変換する（編集画面初期化用）。
 * - lineFlag は boolean → DivConst の数値に変換する
 * - respondentFields は id / no を含めてそのままマッピングする（既存実装温存）
 * - questions / results から id を除いて必要なフィールドのみ抽出する
 */
export function buildEditFormFromResponse(
  response: QuizResponseType
): Partial<QuizEditFormStateType> {
  return {
    title: response.title ?? undefined,
    titleHiddenFlag: response.titleHiddenFlag,
    description: response.description ?? undefined,
    respondentMailDiv: response.respondentMailDiv ?? undefined,
    imageKey: response.imageKey,
    popupImageKey: response.popupImageKey ?? undefined,
    reportMailSubject: response.reportMailSubject ?? undefined,
    reportMailSenderName: response.reportMailSenderName ?? undefined,
    reportMailSignatureHtml: response.reportMailSignatureHtml ?? undefined,
    reportMailDescribeHtml: response.reportMailDescribeHtml ?? undefined,
    quizTemplateId: response.quizTemplateId ?? undefined,
    publishStartAt: response.publishStartAt,
    publishEndAt: response.publishEndAt,
    noIndexFlag: response.noIndexFlag,
    benchmarkContactListId: response.benchmarkContactListId,
    gtmId: response.gtmId,
    respondentFieldGroupId: response.respondentFieldGroupId,
    respondentFields: response.respondentFields.map(field => ({
      ...field,
    })) as unknown as QuizEditFormStateType['respondentFields'],
    lineFlag: response.lineFlag
      ? DivConst.items.lineFlag.enable.value
      : DivConst.items.lineFlag.disable.value,
    lineUrl: response.lineUrl ?? undefined,
    questions: response.questions.map(q => ({
      title: q.title ?? undefined,
      descriptionHtml: q.descriptionHtml ?? undefined,
      reportMailDescriptionHtml: q.reportMailDescriptionHtml ?? undefined,
      questionImageKey: q.questionImageKey ?? undefined,
      answerImageKey: q.answerImageKey ?? undefined,
      options: q.options.map(opt => ({
        title: opt.title ?? undefined,
        correctFlag: opt.correctFlag,
      })),
    })),
    results: response.results.map(r => ({
      scoreThreshold: r.scoreThreshold,
      title: r.title,
      descriptionHtml: r.descriptionHtml,
      imageKey: r.imageKey,
    })),
  };
}

// ------------------------------------------------------------------ //
// mapAiQuestionsToFormQuestions
// ------------------------------------------------------------------ //

/**
 * AI 生成の問題リストをフォーム questions 形式に変換する。
 * - reportMailDescriptionHtml を descriptionHtml から補完する（null の場合は空文字）
 * - questionImageKey / answerImageKey を null で初期化する
 */
export function mapAiQuestionsToFormQuestions(
  aiQuestions: AiQuizQuestion[]
): QuizEditFormStateType['questions'] {
  return aiQuestions.map(q => ({
    ...q,
    reportMailDescriptionHtml: q.descriptionHtml ?? '',
    questionImageKey: null,
    answerImageKey: null,
  })) as unknown as QuizEditFormStateType['questions'];
}

// ------------------------------------------------------------------ //
// buildAiChatCurrentContent
// ------------------------------------------------------------------ //

/**
 * クイズ編集画面のフォーム値を AI チャット送信用の currentContent に変換する。
 * - 出力キーは QuizAiContentInput の全キーのみ（state 余剰キー
 *   reportMailDescriptionHtml / answerImageKey / questions.questionImageKey / results.imageKey は含めない）
 * - 未設定値は `?? null` で必ずキーを送る
 * - questions に `name: 設問N`（N は 1 始まり）を付与する
 */
export function buildAiChatCurrentContent(values: QuizEditFormStateType): QuizAiContentInput {
  return {
    title: values.title ?? null,
    description: values.description ?? null,
    questions: (values.questions ?? []).map((question, index) => ({
      name: `設問${index + 1}`,
      title: question.title ?? null,
      descriptionHtml: question.descriptionHtml ?? null,
      options: (question.options ?? []).map(option => ({
        title: option.title ?? null,
        correctFlag: option.correctFlag ?? null,
      })),
    })),
    results: (values.results ?? []).map(result => ({
      scoreThreshold: result.scoreThreshold ?? null,
      title: result.title ?? null,
      descriptionHtml: result.descriptionHtml ?? null,
    })),
  };
}
